import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

type DimensionalUserSelectionContextValue = {
    selectedKeysByAttribute: Record<string, string[]>;
    activeKeyByAttribute: Record<string, string>;
    overviewKeysByAttribute: Record<string, string[]>;
    selectKey: (attributeName: string, key: string) => void;
    unselectKey: (attributeName: string, key: string) => void;
    setActiveKey: (attributeName: string, key: string) => void;
    setOverviewKeysByAttribute: (next: Record<string, string[]>) => void;
};

const DimensionalUserSelectionContext = createContext<DimensionalUserSelectionContextValue | null>(null);

/**
 * Optional context used by mass-update to record dimension keys the user
 * targeted. Auto-selecting the first key on mount updates `activeKey` (the
 * key currently on screen) but is not a value edit and must not be added
 * to `selectedKeys`. Opening a language in the dropdown is also not enough;
 * `selectKey` is for keys whose value actually changed, and `unselectKey`
 * drops a key that was only viewed.
 */
const DimensionalUserSelectionProvider = ({ children }: PropsWithChildren) => {
    const [selectedKeysByAttribute, setSelectedKeysByAttribute] = useState<Record<string, string[]>>({});
    const [activeKeyByAttribute, setActiveKeyByAttribute] = useState<Record<string, string>>({});
    const [overviewKeysByAttribute, setOverviewKeysByAttributeState] = useState<Record<string, string[]>>({});

    const selectKey = useCallback((attributeName: string, key: string) => {
        setSelectedKeysByAttribute((previous) => {
            const current = previous[attributeName] ?? [];
            if (current.includes(key)) {
                return previous;
            }

            return {
                ...previous,
                [attributeName]: [...current, key],
            };
        });
    }, []);

    const unselectKey = useCallback((attributeName: string, key: string) => {
        setSelectedKeysByAttribute((previous) => {
            const current = previous[attributeName] ?? [];
            if (!current.includes(key)) {
                return previous;
            }

            const nextKeys = current.filter((entry) => entry !== key);
            if (nextKeys.length === 0) {
                const next = { ...previous };
                delete next[attributeName];
                return next;
            }

            return {
                ...previous,
                [attributeName]: nextKeys,
            };
        });
    }, []);

    const setActiveKey = useCallback((attributeName: string, key: string) => {
        setActiveKeyByAttribute((previous) => {
            if (previous[attributeName] === key) {
                return previous;
            }

            return {
                ...previous,
                [attributeName]: key,
            };
        });
    }, []);

    const setOverviewKeysByAttribute = useCallback((next: Record<string, string[]>) => {
        setOverviewKeysByAttributeState(next);
    }, []);

    const value = useMemo(
        () => ({
            selectedKeysByAttribute,
            activeKeyByAttribute,
            overviewKeysByAttribute,
            selectKey,
            unselectKey,
            setActiveKey,
            setOverviewKeysByAttribute,
        }),
        [
            selectedKeysByAttribute,
            activeKeyByAttribute,
            overviewKeysByAttribute,
            selectKey,
            unselectKey,
            setActiveKey,
            setOverviewKeysByAttribute,
        ],
    );

    return (
        <DimensionalUserSelectionContext.Provider value={value}>
            {children}
        </DimensionalUserSelectionContext.Provider>
    );
};

const useDimensionalUserSelection = () => useContext(DimensionalUserSelectionContext);

export { DimensionalUserSelectionProvider, useDimensionalUserSelection };
